import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { shallow } from "zustand/shallow";

import { isFundingSourceCashOnly, isFundingSourceSecuritiesOnly } from "../../../helpers";
import useIntakeFormStore from "../../../store/IntakeForm.store";
import useUserStore from "../../../store/User.store";
import Button from "../../Button";
import Slider from "../../Slider";
import Toolbar from "../../Toolbar";

import "./IntakeFormToolbar.scss";

const IntakeFormToolbar = ({ onSave, isDuplicating }) => {
  const userData = useUserStore((state) => state.data.user);
  const [formState, formOptions, formData] = useIntakeFormStore(
    (state) => [
      state.data.formState,
      state.data.formOptions,
      state.data.formData,
    ],
    shallow
  );

  const [strategyDetail, setStrategyDetail] = useState();
  const [activeSubmit, setActiveSubmit] = useState(false);
  const [isStrategySummaryVisible, setIsStrategySummaryVisible] =
    useState(false);

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const watchedRequiredValues = useWatch({
    control,
    name: ["client", "account", "fundingSrc", "advisorName", "selectStrategy"],
  });

  const watchedSelectStrategy = useWatch({
    control,
    name: "selectStrategy",
  });

  const watchedCashAmount = useWatch({
    control,
    name: "cashAmount",
  });

  const watchedFundingSrc = useWatch({
    control,
    name: "fundingSrc",
  });

  const watchedTaxLotData = useWatch({
    control,
    name: "taxLotData",
  });

  const watchedCustomBlend = useWatch({
    control,
    name: "customBlend",
  });

  useEffect(() => {
    if (formState.strategyCode) {
      setStrategyDetail(
        formOptions.strategyDetails.find(
          (strat) => strat.strategyCode === formState.strategyCode
        )
      );
    }
  }, [formState, formOptions]);

  const currentErrors = Object.keys(errors);

  const getValue = (key) => {
    return strategyDetail?.[key] || "-";
  };

  const hasTaxLotData = (data, watchedFundingSrc) => {
    if (isFundingSourceCashOnly(watchedFundingSrc)) return true;
    return data.length > 0 && data.filter((e) => e.cusip !== "").length > 0;
  };

  const hasCustomBlendData = (strategy) => {
    // console.log("Form state is " + JSON.stringify(formState));
    // console.log("Watched custom blend " + JSON.stringify(watchedCustomBlend));
    if(strategy === "INDCBL" || strategy === "INDCBL7030") {
      return formState.customBlend
    }
    return true;
  }

  useEffect(() => {
    const hasFilledAllRequired = watchedRequiredValues.every(
      (value) => typeof value !== "undefined" && value !== ""
    );
    const isCashAmountValid =
      isFundingSourceSecuritiesOnly(watchedFundingSrc) || watchedCashAmount > 0;
    setActiveSubmit(
      currentErrors.length === 0 &&
        hasFilledAllRequired &&
        (isCashAmountValid &&
          hasTaxLotData(watchedTaxLotData, watchedFundingSrc) &&
          hasCustomBlendData(watchedSelectStrategy))
    );
  }, [
    watchedRequiredValues,
    currentErrors,
    watchedCashAmount,
    watchedFundingSrc,
    watchedTaxLotData,
  ]);

  useEffect(() => {
    setIsStrategySummaryVisible(watchedSelectStrategy?.length > 0);
  }, [watchedSelectStrategy, strategyDetail]);

  return (
    <Toolbar
      title={`${formData ? formData.account : "New Proposal"} Summary`}
      collapsible={true}
      headerCTA={
        (!formData ||
          formState?.status?.toLowerCase() === "draft" ||
          formState?.status?.toLowerCase() === "needs attention" ||
          isDuplicating) && (
          <>
            <Button
              type="button"
              size="small"
              variant="outline"
              className="toolbarHeaderCta"
              disabled={!!currentErrors?.length || formState.isFormLocked}
              onClick={onSave}
            >
              Save Proposal
            </Button>
            <Button
              size="small"
              className="toolbarHeaderCta"
              disabled={!activeSubmit || formState.isFormLocked}
              type="submit"
            >
              Submit Proposal
            </Button>
          </>
        )
      }
      className="intake-toolbar"
    >
      <Slider>
        <div className="toolbar__wrapper">
          <div className="toolbar__section">
            <div className="proposal_owner">
              <dl>
                <div>
                  <dt>Prepared for</dt>
                  <dd>
                    {formData
                      ? formData.client
                      : watchedRequiredValues[0] || "-"}
                  </dd>
                </div>
                <div className="preparedBy">
                  <dt>Prepared by</dt>
                  <dd>
                    {formData
                      ? formData.advisorName
                      : `${userData?.firstName} ${userData?.lastName}`}
                  </dd>
                </div>
              </dl>
            </div>

            {isStrategySummaryVisible && (
              <>
                {strategyDetail?.platform && (
                  <div className="proposal_availability">
                    <div className="proposal_platform">
                      <p>Platform Availability</p>
                      <dl>
                        <dt>
                          For questions about platform availability, please
                          reach out to your sales specialist.
                        </dt>
                      </dl>
                    </div>
                  </div>
                )}
                <div className="proposal_strategy">
                  <p>Strategy Details</p>
                  <dl>
                    <dt>Minimum Credit Quality</dt>
                    <dd>{getValue("minCredQual")}</dd>
                    <dt>Maximum Duration</dt>
                    <dd>{getValue("maxDuration")}</dd>
                    <dt>National / State Option</dt>
                    <dd>{getValue("natStateOpt")}</dd>
                  </dl>
                </div>
                {strategyDetail?.customizations?.length > 0 && (
                  <div className="proposal_customizations">
                    <p>Customizations Allowed</p>
                    <dl>
                      {strategyDetail.customizations.map((cust, idx) => (
                        <div key={idx}>
                          <dt>{cust.displayName}</dt>
                          <dd>{cust.desc}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Slider>
    </Toolbar>
  );
};

IntakeFormToolbar.propTypes = {
  onSave: PropTypes.func,
  isDuplicating: PropTypes.bool,
};

export default IntakeFormToolbar;
